import { MdClose, MdWarning, MdError, MdCheckCircle } from 'react-icons/md';
import { RfqExcelImportPreview } from '../../../utils/rfq-excel.types';
import { ProcurementActionButton, ProcurementPrimaryButton } from '../../../components/shared/ProcurementActionButton';

interface OfferExcelImportModalProps {
  preview: RfqExcelImportPreview;
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function OfferExcelImportModal({
  preview,
  fileName,
  onConfirm,
  onCancel,
}: OfferExcelImportModalProps) {
  const canApply = preview.errors.length === 0 && preview.items.length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#0088cc]/15">
          <div>
            <h3 className="text-lg font-bold text-[#0f172a]">Import RFQ from Excel</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 truncate max-w-md">{fileName}</p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-[#0088cc]/65 hover:text-[hsl(var(--muted-foreground))] rounded-lg hover:bg-[#0088cc]/10 transition-colors"
          >
            <MdClose className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-1">Line Items</p>
              <p className="text-2xl font-black text-[#0f172a]">{preview.summary.itemCount}</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1">Vendors Matched</p>
              <p className="text-2xl font-black text-[#0f172a]">{preview.summary.vendorCount}</p>
            </div>
          </div>

          {preview.summary.matchedFields.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <MdCheckCircle className="w-4 h-4 text-emerald-500" />
                Matched Fields
              </p>
              <div className="flex flex-wrap gap-2">
                {preview.summary.matchedFields.map((f) => (
                  <span
                    key={f}
                    className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[11px] font-bold rounded-lg border border-emerald-100"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {preview.summary.unmatchedFields.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <MdWarning className="w-4 h-4 text-amber-500" />
                Unmatched Fields
              </p>
              <div className="flex flex-wrap gap-2">
                {preview.summary.unmatchedFields.map((f) => (
                  <span
                    key={f}
                    className="px-2.5 py-1 bg-amber-50 text-amber-700 text-[11px] font-bold rounded-lg border border-amber-100"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {preview.errors.length > 0 && (
            <div className="p-4 bg-red-50 rounded-xl border border-red-100">
              <p className="text-[11px] font-bold text-red-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <MdError className="w-4 h-4" />
                Errors
              </p>
              <ul className="space-y-1">
                {preview.errors.map((e, i) => (
                  <li key={i} className="text-sm text-red-700">{e}</li>
                ))}
              </ul>
            </div>
          )}

          {preview.warnings.length > 0 && (
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
              <p className="text-[11px] font-bold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <MdWarning className="w-4 h-4" />
                Warnings
              </p>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {preview.warnings.map((w, i) => (
                  <li key={i} className="text-sm text-amber-800">{w}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Applying will replace all current form data and items with the imported values.
            Attachments are not affected.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-[#0088cc]/15 bg-[#0088cc]/5 dark:bg-slate-900/50">
          <ProcurementActionButton size="modal" onClick={onCancel}>
            Cancel
          </ProcurementActionButton>
          <ProcurementPrimaryButton size="modal" onClick={onConfirm} disabled={!canApply}>
            Apply to Form
          </ProcurementPrimaryButton>
        </div>
      </div>
    </div>
  );
}
